(() => {
  const cleanups = new WeakMap();
  const archiveDate = new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });

  function parseLocalAssets(root) {
    const script = root.querySelector("[data-frml-archive-local-assets]");
    if (!script) return {};
    try {
      return JSON.parse(script.textContent || "{}");
    } catch {
      return {};
    }
  }

  function mount(root) {
    if (!root || cleanups.has(root)) return;

    const indexContainer = root.querySelector("[data-frml-archive-index]");
    const releaseContainer = root.querySelector("[data-frml-archive-release]");
    const randomButton = root.querySelector("[data-frml-archive-random]");
    const groups = Array.isArray(window.FRML_ARCHIVE_DATA)
      ? window.FRML_ARCHIVE_DATA
      : [];
    if (!indexContainer || !releaseContainer || !randomButton || !groups.length) {
      return;
    }

    const localAssets = parseLocalAssets(root);
    const originalTitle = document.title;

    const imageUrl = (image, width = 1200) => {
      if (image.url.startsWith("asset://")) {
        return localAssets[image.url.slice("asset://".length)] || "";
      }
      const separator = image.url.includes("?") ? "&" : "?";
      return `${image.url}${separator}width=${width}`;
    };

    const createImage = (image, alt, options = {}) => {
      const element = document.createElement("img");
      element.src = imageUrl(image, options.width);
      element.alt = image.altText || alt;
      element.width = image.width;
      element.height = image.height;
      element.loading = options.eager ? "eager" : "lazy";
      element.decoding = "async";
      if (options.eager) element.fetchPriority = "high";
      return element;
    };

    const releaseUrl = (family) => {
      const url = new URL(window.location.href);
      url.searchParams.set("release", family);
      return `${url.pathname}${url.search}${url.hash}`;
    };

    const renderIndex = () => {
      const fragment = document.createDocumentFragment();
      groups.forEach((group, groupIndex) => {
        const section = document.createElement("section");
        section.className = "archived-product";
        section.id = `archive-${group.family}`;

        const heading = document.createElement("header");
        heading.className = "archived-product-heading";
        const title = document.createElement("h2");
        const link = document.createElement("a");
        link.href = releaseUrl(group.family);
        link.dataset.archiveReleaseLink = group.family;
        link.textContent = group.title;
        title.append(link);
        const time = document.createElement("time");
        time.dateTime = group.createdAt;
        time.textContent = archiveDate.format(new Date(group.createdAt));
        heading.append(title, time);

        const gallery = document.createElement("div");
        gallery.className = "archived-product-gallery";
        group.images.forEach((image, imageIndex) => {
          gallery.append(
            createImage(image, group.title, {
              eager: groupIndex === 0 && imageIndex === 0,
              width: 1000,
            }),
          );
        });
        section.append(heading, gallery);
        fragment.append(section);
      });
      indexContainer.replaceChildren(fragment);
    };

    const showIndex = ({ updateHistory = false } = {}) => {
      indexContainer.hidden = false;
      releaseContainer.hidden = true;
      randomButton.hidden = false;
      document.title = originalTitle;
      if (updateHistory) {
        const url = new URL(window.location.href);
        url.searchParams.delete("release");
        history.pushState({}, "", `${url.pathname}${url.search}${url.hash}`);
      }
    };

    const makeReleaseLink = (group, direction) => {
      const link = document.createElement("a");
      link.href = releaseUrl(group.family);
      link.dataset.archiveReleaseLink = group.family;
      link.setAttribute("aria-label", `${direction}: ${group.title}`);
      link.textContent = group.title;
      return link;
    };

    const showRelease = (family, { updateHistory = false } = {}) => {
      const groupIndex = groups.findIndex((group) => group.family === family);
      if (groupIndex < 0) {
        showIndex({ updateHistory });
        return;
      }

      const group = groups[groupIndex];
      const newer = groups[groupIndex - 1];
      const older = groups[groupIndex + 1];
      const heading = document.createElement("header");
      heading.className = "archive-release-heading";
      const title = document.createElement("h1");
      title.textContent = group.title;
      const time = document.createElement("time");
      time.dateTime = group.createdAt;
      time.textContent = archiveDate.format(new Date(group.createdAt));
      heading.append(title, time);

      const gallery = document.createElement("div");
      gallery.className = "archive-release-gallery";
      group.images.forEach((image, imageIndex) => {
        gallery.append(
          createImage(image, group.title, {
            eager: imageIndex === 0,
            width: 1600,
          }),
        );
      });

      const navigation = document.createElement("nav");
      navigation.className = "archive-release-nav";
      navigation.setAttribute("aria-label", "archive releases");
      if (newer) navigation.append(makeReleaseLink(newer, "newer"));
      if (older) navigation.append(makeReleaseLink(older, "older"));

      releaseContainer.replaceChildren(heading, gallery, navigation);
      indexContainer.hidden = true;
      releaseContainer.hidden = false;
      randomButton.hidden = true;
      document.title = `${group.title} – ${originalTitle}`;
      window.scrollTo(0, 0);

      if (updateHistory) {
        history.pushState({}, "", releaseUrl(group.family));
      }
    };

    const renderRoute = () => {
      const family = new URL(window.location.href).searchParams.get("release");
      if (family) {
        showRelease(family);
      } else {
        showIndex();
      }
    };

    const handleClick = (event) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const link = event.target.closest("[data-archive-release-link]");
      if (!link || !root.contains(link)) return;
      event.preventDefault();
      showRelease(link.dataset.archiveReleaseLink, { updateHistory: true });
    };

    const handleRandom = () => {
      showIndex();
      const group = groups[Math.floor(Math.random() * groups.length)];
      requestAnimationFrame(() => {
        document
          .getElementById(`archive-${group.family}`)
          ?.scrollIntoView({ behavior: "smooth" });
      });
    };

    renderIndex();
    renderRoute();
    root.addEventListener("click", handleClick);
    randomButton.addEventListener("click", handleRandom);
    window.addEventListener("popstate", renderRoute);

    const cleanup = () => {
      document.title = originalTitle;
      root.removeEventListener("click", handleClick);
      randomButton.removeEventListener("click", handleRandom);
      window.removeEventListener("popstate", renderRoute);
      cleanups.delete(root);
    };
    cleanups.set(root, cleanup);
  }

  function mountAll(scope = document) {
    scope.querySelectorAll("[data-frml-archive]").forEach(mount);
  }

  mountAll();
  document.addEventListener("shopify:section:load", (event) =>
    mountAll(event.target),
  );
  document.addEventListener("shopify:section:unload", (event) => {
    event.target.querySelectorAll("[data-frml-archive]").forEach((root) => {
      cleanups.get(root)?.();
    });
  });
})();
